import { SIXTEENTHS } from "odyssey-shared";
import type { FightSounds } from "./audio/cues.ts";
import type { FeetPoint } from "./fight/targeting.ts";
import { PageGround } from "./ground/page-ground.ts";
import { PageTargets } from "./page-targets.ts";
import type { RemoteCharacters } from "./remote-characters.ts";
import { PAGE_RULES } from "./rules.ts";
import type { SessionCore } from "./session-types.ts";

/**
 * Makes the player's target (P2.2-T3): a click's, Tab's or Escape's selection, sent as `target` once the world is shown
 * and the player is not frozen, named in every act and shown in the HUD's target frame. It reads where the target
 * stands from the connection's view, the other characters as `remotes` draws them, the things as the world holds them,
 * and the local character as the scene draws it.
 *
 * @param core What the session builds first.
 * @param remotes The other players' characters.
 * @param ownDrawn Where the local character is drawn, written into the object given; false before it is drawn.
 * @returns The targets.
 */
export function createTargets(core: SessionCore, remotes: RemoteCharacters, ownDrawn: (out: FeetPoint) => boolean): PageTargets {
  const { options, host, state, connection, gate } = core;
  return new PageTargets({
    send: (handle) => state.revealed && !gate.refuses() && (connection()?.sendTarget(handle) ?? false),
    entities: () => connection()?.view.entities ?? null,
    own: () => state.handle,
    scale: () => {
      const tileSize = host.world()?.tileSize ?? 0;
      return tileSize / SIXTEENTHS;
    },
    ownDrawn,
    drawn: (handle, out) => remotes.box(handle, out),
    ground: (handle, out) => host.world()?.things.ground(handle, out) ?? false,
    hud: options.hud,
    ...(options.bar === undefined ? {} : { bar: options.bar }),
    creatureName: (appearance) => host.world()?.looks[appearance]?.name ?? null,
  });
}

/**
 * Makes the page's ground items (P2.8-T65): its own items' marks, their drawing and the pick-up E and a click ask,
 * sent once the world is shown and the player is not frozen, refused with the fight's refusal sound.
 *
 * @param core What the session builds first.
 * @param fightSounds The fight's sounds, or undefined without sounds.
 * @returns The ground items.
 */
export function createGround(core: SessionCore, fightSounds: FightSounds | undefined): PageGround {
  const { options, host, state, connection, local, gate } = core;
  const sounds = options.sounds;
  return new PageGround(
    {
      entities: () => connection()?.view.entities ?? null,
      feet: () => local.prediction?.predicted,
      send: (handle) => state.revealed && !gate.refuses() && (connection()?.sendPickUp(handle) ?? false),
      half: () => host.world()?.ground.half ?? 0,
      refused: () => fightSounds?.refused(),
      say: (line) => host.messageLine.say(line),
    },
    () => host.world()?.ground ?? null,
    PAGE_RULES,
    sounds === undefined ? null : (hook, dx, dy) => sounds.groundLanded(hook, dx, dy),
  );
}
